import {
  AreaTooltip,
  Box,
  Disclaimer,
  Div,
  H5,
  P,
  Pill,
  lightTheme,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
} from 'recharts';
import type { AnnouncementViewsPerDay } from '../../../types/api/announcements.types';

export const VIEW_RANGES = [7, 30, 90] as const;
export type ViewRange = (typeof VIEW_RANGES)[number];

type Props = {
  days: AnnouncementViewsPerDay[];
  range: ViewRange;
  onRange: (range: ViewRange) => void;
  /** All time, to tell "nobody looked" from "looked before we counted". */
  seenCount: number;
};

const dayLabel = (date: string, withYear = false) =>
  DateTime.fromISO(date).toFormat(withYear ? 'LLL d, yyyy' : 'LLL d');

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/** "12 views · 9 users · 1 dismissed", over the day it is about. */
const DayTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  const day = payload?.[0]?.payload as AnnouncementViewsPerDay | undefined;
  if (!active || !day) return null;

  return (
    <AreaTooltip>
      <Disclaimer $m="0 0 4px" $color={lightTheme.lightGray}>
        {dayLabel(day.date, true)}
      </Disclaimer>
      <P $m="0">
        {plural(day.views, 'view')} · {plural(day.uniqueViewers, 'user')} ·{' '}
        {day.dismissals} dismissed
      </P>
    </AreaTooltip>
  );
};

/**
 * Views per day as bars, with the range switch in the card's corner. Only the
 * first and last day are labelled: at 90 bars any more would overlap, and the
 * tooltip has the exact day anyway.
 */
export const AnnouncementViewsChart = ({
  days,
  range,
  onRange,
  seenCount,
}: Props) => {
  const empty = days.every((day) => day.views === 0 && day.dismissals === 0);
  const edges = days.length ? [days[0].date, days[days.length - 1].date] : [];

  return (
    <Box $flex $column $gap="12px" $minWidth="0">
      <Div $flex $between $alignCenter $gap="8px">
        <H5 $m="0">Views per day</H5>
        <Div $flex $gap="6px">
          {VIEW_RANGES.map((value) => (
            <Pill
              key={value}
              $background={
                value === range ? lightTheme.primary : lightTheme.gray
              }
              $minWidth="0"
              $minWidthSM="0"
              $pointer
              onClick={() => onRange(value)}
            >
              <Disclaimer
                $m="0"
                $color={
                  value === range ? lightTheme.white : lightTheme.darkGray
                }
              >
                {value}d
              </Disclaimer>
            </Pill>
          ))}
        </Div>
      </Div>

      <Div $height="180px" $heightSM="180px" $relative>
        <ResponsiveContainer width="100%" height="100%">
          {/* Side margins: the edge dates centre on their bar and would
              otherwise hang off the card. */}
          <BarChart
            data={days}
            margin={{ top: 4, right: 20, bottom: 0, left: 20 }}
          >
            <XAxis
              dataKey="date"
              ticks={edges}
              tickFormatter={(date: string) => dayLabel(date)}
              tickLine={false}
              axisLine={{ stroke: lightTheme.gray }}
              tick={{ fontSize: 12, fill: lightTheme.lightGray }}
              interval={0}
            />
            <Tooltip
              content={<DayTooltip />}
              cursor={{ fill: `${lightTheme.primary}14` }}
            />
            <Bar
              dataKey="views"
              fill={lightTheme.primary}
              radius={[4, 4, 0, 0]}
              minPointSize={empty ? 0 : 2}
            />
          </BarChart>
        </ResponsiveContainer>

        {empty && (
          <Div
            $absolute
            $top="0"
            $left="0"
            $right="0"
            $bottom="24px"
            $flex
            $center
            $p="0 16px"
          >
            <Disclaimer $m="0" $color={lightTheme.lightGray} $center>
              {seenCount
                ? 'No views in this range. Daily numbers are only counted since this feature went live; earlier views are in the list below.'
                : 'No views yet.'}
            </Disclaimer>
          </Div>
        )}
      </Div>
    </Box>
  );
};
